import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { capitalize } from "@utils/toolBox";
import { useSurveyStore } from "@stores/survey/survey";
import { useCardsStore } from "@stores/survey/cards";
import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { useSurveyQuestionsStore } from "@stores/surveyQuestions";
import type { AnswerQuota, Card, Quota, QuotaType, QuotaUnit } from "@/types";
import { captureEvent } from "@/lib/analytics";

const QUOTA_ELIGIBLE_TYPES = ["single", "multiple", "image_choice", "image_choice_multiple", "hidden"];
const MULTI_ANSWER_TYPES = ["multiple", "image_choice_multiple"];
const DEFAULT_GLOBAL_MIN_TOLERANCE = 20;
const DEFAULT_GLOBAL_MAX_TOLERANCE = null;

export const useQuotasStore = defineStore("quotas", () => {
  const surveyStore = useSurveyStore();
  const { token } = storeToRefs(surveyStore);
  const cardsStore = useCardsStore();

  const surveyTargetsStore = useSurveyTargetsStore();
  const { selectedTarget } = storeToRefs(surveyTargetsStore);

  const surveyLocalesStore = useSurveyLocalesStore();
  const { localizeText } = surveyLocalesStore;

  const surveyQuestionsStore = useSurveyQuestionsStore();
  const { currentQuestions } = storeToRefs(surveyQuestionsStore);

  const quotas = ref<Quota[]>([]);
  const quotaTypes = ref<QuotaType[]>([]);
  const units = ref<QuotaUnit[]>([]);
  const deletedQuotaIds = ref<number[]>([]);
  const initialQuotasById = ref<Record<number, string>>({});
  const errorsByQuota = ref(new Map<Quota, { messages: string[]; snapshot: string }>());
  const deletionErrors = ref<string[]>([]);

  const isErrorCurrent = (quota: Quota) => errorsByQuota.value.get(quota)?.snapshot === JSON.stringify(quota);
  const hasErrors = computed(
    () => deletionErrors.value.length > 0 || [...errorsByQuota.value.keys()].some(isErrorCurrent),
  );
  const quotaErrors = (quota: Quota) => (isErrorCurrent(quota) ? (errorsByQuota.value.get(quota)?.messages ?? []) : []);

  const cards = computed<Card[]>(() => cardsStore.cards);

  const toOption = (value: string) => ({ value, label: capitalize(value.replace(/_/g, " ")) });
  const quotaTypeOptions = computed(() => quotaTypes.value.map(toOption));
  const quotaUnitOptions = computed(() => units.value.map(toOption));

  async function fetchQuotas() {
    try {
      const { response, data } = await useMyFetch(`surveys/${token.value}/quotas`).json<{
        quotas: Quota[];
        quota_types: QuotaType[];
        units: QuotaUnit[];
      }>();
      if (response.value.ok) {
        quotas.value = data.value.quotas;
        quotas.value.forEach((quota) => (quota.answer_quotas = buildAnswerQuotaGrid(quota)));
        quotaTypes.value = data.value.quota_types;
        units.value = data.value.units;
        deletedQuotaIds.value = [];
        initialQuotasById.value = Object.fromEntries(quotas.value.map((q) => [q.id, JSON.stringify(q)]));
        errorsByQuota.value = new Map();
        deletionErrors.value = [];
      }
    } catch (err) {
      handleError(err);
    }
  }

  const hasQuotaChanged = (quota: Quota) => !quota.id || JSON.stringify(quota) !== initialQuotasById.value[quota.id];

  const hasChanged = computed(() => deletedQuotaIds.value.length > 0 || quotas.value.some(hasQuotaChanged));

  const currentQuotas = computed<Quota[]>(() => {
    const target = selectedTarget.value;
    if (!target?.token) return [];
    return quotas.value.filter(
      (q) => (q.survey_target_id != null && q.survey_target_id === target.id) || q.survey_target_token === target.token,
    );
  });

  const questionOptions = computed(() =>
    currentQuestions.value
      .filter((q) => QUOTA_ELIGIBLE_TYPES.includes(q.question_type) && q.name)
      .map((q) => ({ label: q.name, value: q.id })),
  );

  const cardFor = (quota: Quota): Card | undefined => cards.value.find((c) => c.token === quota.card_token);

  const isMultiAnswerQuota = (quota: Quota) =>
    MULTI_ANSWER_TYPES.includes(
      currentQuestions.value.find((question) => question.id === quota.question_id)?.question_type ?? "",
    );

  const hasLimit = (answerQuota: AnswerQuota) => (answerQuota.target_value ?? 0) > 0;
  const hasAnyLimit = (quota: Quota) => quota.answer_quotas.some(hasLimit);

  const buildAnswerQuotaGrid = (quota: Quota): AnswerQuota[] => {
    const persisted = quota.answer_quotas;
    const answers = cardFor(quota)?.specs?.answers ?? [];
    if (!answers.length) return persisted;
    const grid = answers.map((answer, i) => {
      const line = persisted.find((aq) =>
        aq.answer_token && answer.token ? aq.answer_token === answer.token : aq.answer === (i + 1).toString(),
      );
      return (
        line ?? {
          answer: (i + 1).toString(),
          answer_token: answer.token ?? null,
          target_value: 0,
          tolerance_min_percent: null,
          tolerance_max_percent: null,
        }
      );
    });
    return [...grid, ...persisted.filter((aq) => !grid.includes(aq))];
  };

  const answerTitle = (cardToken: Quota["card_token"], answer: string): string => {
    const cardAnswer = cards.value.find((c) => c.token === cardToken)?.specs?.answers?.[Number(answer) - 1];
    return cardAnswer ? (localizeText(cardAnswer.title) as string) : "";
  };

  const addQuota = () => {
    quotas.value.push({
      question_id: null,
      card_token: null,
      survey_target_token: selectedTarget.value?.token,
      quota_type: "regular",
      unit: "percentage",
      global_min_tolerance: DEFAULT_GLOBAL_MIN_TOLERANCE,
      global_max_tolerance: DEFAULT_GLOBAL_MAX_TOLERANCE,
      answer_quotas: [],
    } as Quota);
  };

  const removeQuota = (quota: Quota) => {
    if (quota.id) deletedQuotaIds.value.push(quota.id);
    quotas.value.splice(quotas.value.indexOf(quota), 1);
    errorsByQuota.value.delete(quota);
  };

  const discardQuotasFor = (targetToken: string) => {
    quotas.value.filter((q) => !q.id && q.survey_target_token === targetToken).forEach(removeQuota);
  };

  const resolvedTargetId = (quota: Quota) =>
    quota.survey_target_id ?? surveyTargetsStore.currentTargets.find((t) => t.token === quota.survey_target_token)?.id;

  const setQuestion = (quota: Quota, questionId: number) => {
    const question = currentQuestions.value.find((q) => q.id === questionId);
    if (quota.id) {
      deletedQuotaIds.value.push(quota.id);
      quota.id = undefined;
    }
    quota.question_id = questionId;
    quota.card_token = question?.card_token ?? null;
    quota.answer_quotas = [];
    quota.answer_quotas = buildAnswerQuotaGrid(quota);
  };

  const toPayload = (quota: Quota) => ({
    quota: {
      question_id: quota.question_id,
      survey_target_id: resolvedTargetId(quota),
      quota_type: quota.quota_type,
      unit: quota.unit,
      global_min_tolerance: quota.global_min_tolerance,
      global_max_tolerance: quota.global_max_tolerance,
      answer_quotas_attributes: quota.answer_quotas
        .filter((aq) => aq.id || hasLimit(aq))
        .map((aq) =>
          hasLimit(aq)
            ? {
                id: aq.id,
                answer: aq.answer,
                answer_token: aq.answer_token,
                target_value: aq.target_value,
                tolerance_min_percent: aq.tolerance_min_percent,
                tolerance_max_percent: aq.tolerance_max_percent,
              }
            : { id: aq.id, _destroy: true },
        ),
    },
  });

  const toErrorMessages = (error: unknown): string[] =>
    Array.isArray(error) ? error.map(String) : typeof error === "string" ? [error] : ["Quota could not be saved"];

  async function save() {
    errorsByQuota.value = new Map();
    deletionErrors.value = [];
    try {
      quotas.value.filter((quota) => quota.id && !hasAnyLimit(quota)).forEach(removeQuota);
      const deletions = await Promise.all(deletedQuotaIds.value.map((id) => useMyFetch(`quotas/${id}`).delete()));
      deletedQuotaIds.value = deletedQuotaIds.value.filter(
        (_, i) => !deletions[i].response.value.ok && deletions[i].response.value.status !== 404,
      );
      deletionErrors.value = deletedQuotaIds.value.map(() => "Quota could not be deleted");
      const quotasToSave = quotas.value.filter(
        (q) => q.question_id && hasAnyLimit(q) && hasQuotaChanged(q) && resolvedTargetId(q) != null,
      );
      const upserts = await Promise.all(
        quotasToSave.map((quota) =>
          quota.id
            ? useMyFetch(`quotas/${quota.id}`).patch(toPayload(quota)).json()
            : useMyFetch(`surveys/${token.value}/quotas`).post(toPayload(quota)).json(),
        ),
      );
      upserts.forEach(({ response, error, data }, i) => {
        const quota = quotasToSave[i];
        if (!response.value.ok) {
          errorsByQuota.value.set(quota, { messages: toErrorMessages(error.value), snapshot: JSON.stringify(quota) });
          return;
        }
        Object.assign(quota, data.value);
        quota.answer_quotas = buildAnswerQuotaGrid(quota);
        if (quota.id) initialQuotasById.value[quota.id] = JSON.stringify(quota);
      });
      const hasPendingQuotas = quotas.value.some(
        (q) => q.question_id && hasAnyLimit(q) && resolvedTargetId(q) == null,
      );
      if (hasErrors.value || hasPendingQuotas) return false;

      captureEvent("quotas_saved", { quotas_count: quotasToSave.length });

      await fetchQuotas();
      return true;
    } catch (err) {
      handleError(err);
      return false;
    }
  }

  return {
    quotas,
    currentQuotas,
    questionOptions,
    quotaTypeOptions,
    quotaUnitOptions,
    hasChanged,
    hasErrors,
    quotaErrors,
    answerTitle,
    isMultiAnswerQuota,
    fetchQuotas,
    addQuota,
    removeQuota,
    discardQuotasFor,
    setQuestion,
    save,
  };
});
